import { matrixCholeskySolver } from "../matrix/index.js";
import { addWeights } from "../utils/addWeights.js";
import { createSystemMatrix } from "../utils/createSystemMatrix.js";
import { calculateAdaptiveWeights } from "../utils/index.js";
import { xEnsureFloat64 } from "./xEnsureFloat64.js";
import { xMultiply } from "./xMultiply.js";
/**
 * Computes the baseline points for the given data using an iterative smoothing algorithm.
 * @param yData - The input data array.
 * @param options - The options for baseline computation.
 * @returns - The computed baseline points.
 */
export function xWhittakerSmoother(yData, options = {}) {
    const { lambda = 100, maxIterations = 100, tolerance = 1e-6, factorStd = 3, learningRate = 0.5, } = options;
    const size = yData.length;
    // eslint-disable-next-line prefer-const
    let { controlPoints, weights } = getWeightsAndControlPoints(yData, options);
    const prevBaseline = new Float64Array(size);
    let iteration = 0;
    let delta = Infinity;
    let baseline = xEnsureFloat64(yData);
    const upperTriangularNonZeros = createSystemMatrix(size, lambda);
    while (iteration < maxIterations && delta > tolerance) {
        const { leftHandSide, rightHandSide } = addWeights(upperTriangularNonZeros, yData, weights);
        const cho = matrixCholeskySolver(leftHandSide, size);
        if (!cho) {
            return baseline;
        }
        const newBaseline = cho(rightHandSide);
        weights = calculateAdaptiveWeights(yData, newBaseline, weights, {
            controlPoints,
            learningRate,
            factorStd,
        });
        delta = calculateDelta(newBaseline, prevBaseline, size);
        prevBaseline.set(newBaseline);
        baseline = xEnsureFloat64(newBaseline);
        iteration++;
    }
    return baseline;
}
/**
 * Calculates the delta between the current and previous baseline.
 * @param baseline - The current baseline array.
 * @param prevBaseline - The previous baseline array.
 * @param n - The length of the arrays.
 * @returns - The calculated delta value.
 */
function calculateDelta(baseline, prevBaseline, n) {
    let sum = 0;
    for (let i = 0; i < n; i++) {
        sum += (baseline[i] - prevBaseline[i]) ** 2;
    }
    return Math.sqrt(sum / n);
}
/**
 * Retrieves the control points and weights for the given data, the weights are modified multiplication of controlPoints if it exists.
 * @param  y - The input data array.
 * @param  options - The options for control points and weights.
 * @returns - The control points and modified weights.
 */
function getWeightsAndControlPoints(y, options = {}) {
    const { length } = y;
    const { controlPoints } = options;
    const { weights = Float64Array.from({ length }).fill(1) } = options;
    if (controlPoints && controlPoints.length !== y.length) {
        throw new RangeError('controlPoints should match the length with X');
    }
    else if (weights.length !== y.length) {
        throw new RangeError('weights should match the length with X');
    }
    return {
        weights: controlPoints ? xMultiply(weights, controlPoints) : weights,
        controlPoints,
    };
}
/**
 * @deprecated Use xWhittakerSmoother instead.
 * TODO: Remove in next major version.
 */
export const xWhitakerSmoother = xWhittakerSmoother;
//# sourceMappingURL=xWhittakerSmoother.js.map